// Wire contract for the Event Manager half of the launcher API
// (`mini-apps/launcher/event-manager-api.ts`), read by `event-manager-client.ts`.
// Types are `z.infer`; the client re-exports them.
//
// Checked against prod rows, not the old hand-written interfaces:
//   - the routes return raw pg rows, so `numeric` columns (`score`,
//     `arr_active_subscriptions`, `arr_opportunity`) arrive as JSON strings.
//     `wireNumber` turns them into the `number` the UI always assumed.
//   - `date` / `timestamptz` columns arrive as ISO strings. A `date` can arrive as
//     `…T00:00:00.000Z` (UTC midnight); `wireDateOnly` trims it to `YYYY-MM-DD` so a
//     local-time `new Date(v)` can't shift it to the prior day for US readers.
//   - `PATCH .../event-member/:id` and `.../seat` answer with the bare
//     `launcher_event_members` row (`RETURNING *`): no SFDC join columns. That
//     is `EventMemberRowSchema`; the list route adds the join (`EventMemberSchema`).
//   - Objects are loose so an unmodeled column survives `{ ...old, ...updated }`.
import { z } from 'zod';
import { SalesforceCampaignMemberStatusSchema } from '../_shared/sfdc-member-status-schema';

/** A Postgres `numeric` arrives as a JSON string; accept both and hand back a number. */
const wireNumber = z.union([z.number(), z.string().min(1).transform(Number).pipe(z.number())]);

/** A Postgres `date` column: a UTC-midnight timestamp is trimmed to its `YYYY-MM-DD`; anything else passes through. */
const wireDateOnly = z
  .string()
  .nullable()
  .transform(v => (v == null ? v : /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/.test(v) ? v.slice(0, 10) : v));

const text = z.string().nullable();
const flag = z.boolean().nullable();

// ── Event members ────────────────────────────────────────────────────────────

/** `launcher_event_members` — the columns the PATCH / seat routes return. */
export const EventMemberRowSchema = z.looseObject({
  id: z.number(),
  campaign_id: z.string(),
  campaign_member_id: z.string(),
  role: z.string(),
  table_id: z.number().nullable(),
  seat_position: z.number().nullable(),
  attending_status: z.string(),
  dietary_restrictions: text,
  cal_invite_sent: z.boolean(),
  cal_invite_accepted: z.boolean(),
  owner_exclusion: z.boolean(),
  owner_priority: z.boolean(),
  intel_notes: text,
  general_notes: text,
  goer_notes: text,
  follow_up_owner: text,
  score: wireNumber.nullable(),
  recommendation: text,
  reasoning: text,
  approved: flag,
  gtme_notes: text,
  primary_met_with: text,
  secondary_met_with: text,
  tertiary_met_with: text,
  last_follow_up_date: wireDateOnly,
  custom_fields: z.record(z.string(), z.unknown()),
  created_at: z.string(),
  updated_at: z.string(),
});
export type EventMemberRow = z.infer<typeof EventMemberRowSchema>;

/** Row + the SFDC campaign-member join `GET .../event-members` adds. */
export const EventMemberSchema = EventMemberRowSchema.extend({
  contact_name: text,
  contact_title: text,
  contact_email: text,
  account_name: text,
  cm_status: text,
  contact_id: text,
  contact_linkedin: text,
  product_tier: text,
  plan_interval: text,
  account_type: text,
  gtme_owner_name: text,
  gtme_owner_email: text,
  gs_owner_name: text,
  gs_owner_email: text,
  claydr_name: text,
  claydr_email: text,
  sales_tier: text,
  arr_active_subscriptions: wireNumber.nullable(),
  arr_opportunity: wireNumber.nullable(),
  upcoming_renewal_close_date: wireDateOnly,
  number_of_open_opps: z.number().nullable(),
  last_gtme_meeting_date: wireDateOnly,
  last_gs_meeting_date: wireDateOnly,
  last_claydr_meeting_date: wireDateOnly,
  suppressed: flag,
  suppression_reasons: text,
  account_suppressed: flag,
  account_suppression_reasons: text,
  qualification_status: text,
  last_opportunity_stage: text,
});
export type EventMember = z.infer<typeof EventMemberSchema>;
export const EventMembersSchema = z.array(EventMemberSchema);

/** `PATCH .../event-member/:id` echoes the row plus `cm_status` when it was in the patch. */
export const PatchEventMemberResponseSchema = EventMemberRowSchema.extend({ cm_status: z.string().optional() });

// ── Event tables / views ─────────────────────────────────────────────────────

export const EventTableSchema = z.looseObject({
  id: z.number(),
  campaign_id: z.string(),
  table_name: z.string(),
  capacity: z.number(),
  sort_order: z.number(),
  created_at: z.string(),
});
export type EventTable = z.infer<typeof EventTableSchema>;
export const EventTablesSchema = z.array(EventTableSchema);

export const EventViewSchema = z.looseObject({
  id: z.number(),
  campaign_id: z.string(),
  name: z.string(),
  slug: z.string(),
  columns: z.array(z.string()),
  column_widths: z.record(z.string(), z.number()),
  is_default: z.boolean(),
  created_by: text,
});
export type EventView = z.infer<typeof EventViewSchema>;
export const EventViewsSchema = z.array(EventViewSchema);

// ── Follow-up ────────────────────────────────────────────────────────────────

export const FollowUpRowSchema = z.looseObject({
  contact_name: text,
  contact_id: text,
  contact_email: text,
  contact_title: text,
  contact_linkedin: text,
  contact_account_id: text,
  account_name: text,
  account_type: text,
  sales_tier: text,
  product_tier: text,
  plan_interval: text,
  number_of_open_opps: z.number().nullable(),
  gtme_owner_name: text,
  gs_owner_name: text,
  claydr_name: text,
  dispatch_log_id: z.string(),
  bucket: text,
  channel: text,
  sender_email: text,
  subject: text,
  body: text,
  result: z.string(),
  result_status: text,
  result_data: z.record(z.string(), z.unknown()).nullable(),
  dispatched_at: z.string(),
  result_at: text,
});
export type FollowUpRow = z.infer<typeof FollowUpRowSchema>;
export const FollowUpRowsSchema = z.array(FollowUpRowSchema);

// ── Add-member batches ───────────────────────────────────────────────────────

export const AddMemberBatchRowSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  work_email: z.string(),
  status: z.string(),
  notes: text,
  result: z.enum(['queued', 'processing', 'sent', 'failed', 'cancelled']),
  error: text,
  created_at: z.string(),
  processing_at: text,
  completed_at: text,
});
export type AddMemberBatchRow = z.infer<typeof AddMemberBatchRowSchema>;

export const AddMemberBatchStatusSchema = z.object({
  batch_id: z.string(),
  rows: z.array(AddMemberBatchRowSchema),
  summary: z.object({
    total: z.number(),
    queued: z.number(),
    processing: z.number(),
    sent: z.number(),
    failed: z.number(),
    cancelled: z.number(),
  }),
});
export type AddMemberBatchStatus = z.infer<typeof AddMemberBatchStatusSchema>;

// ── Statuses / SFDC users ────────────────────────────────────────────────────

export const CampaignMemberStatusesSchema = z.array(SalesforceCampaignMemberStatusSchema);
export type CampaignMemberStatus = z.infer<typeof SalesforceCampaignMemberStatusSchema>;

/**
 * The slice of `src/shared/schemas/sfdc-user.ts#SfdcUserSchema` the launcher UI
 * reads. Not that schema itself: it binds `userType` to Prisma's enum, which a
 * frontend bundle must not import (see `_shared/sfdc-users.types.ts`), and no
 * launcher component reads `userType`.
 */
export const LauncherSfdcUserSchema = z.looseObject({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  roleLabel: z.string(),
  isMarketingUser: z.boolean(),
});
export type SfdcUser = z.infer<typeof LauncherSfdcUserSchema>;
export const LauncherSfdcUsersSchema = z.array(LauncherSfdcUserSchema);
